import { useQuery } from '@apollo/client';
import { Dayjs } from 'dayjs';
import { handleSeoul } from 'common/modal/chart/oversea/chartBar';
import {
  ISearchParamItinerary,
  ISearchParams,
  TripType,
} from 'contexts/searchParams';
import GET_FLIGHT_FARE from '../components/common/modal/chart/oversea/flightFare.gql';

interface IFare {
  date: string;
  fares: {
    stops: number;
    fare: number;
  }[];
}

type CustomDate = {
  date: string;
  name: string;
  classname?: string;
};

const useFarePrices = (
  searchParams: ISearchParams,
  isDomestic: boolean,
  date: Dayjs | null,
) => {
  const { tripType, itinerary } = searchParams;
  let prevItinerary: ISearchParamItinerary[] | null = null;
  let prevDepDate: string | null = null;

  const dep = date?.format('YYYYMMDD') || itinerary[0]?.departureDate || null;

  const { departureCity, arrivalCity } = itinerary[0];

  const isSkip =
    isDomestic ||
    (tripType !== TripType.OW && tripType !== TripType.RT) ||
    !dep ||
    (dep === prevDepDate && itinerary === prevItinerary) ||
    !arrivalCity?.code;

  prevDepDate = dep;
  prevItinerary = itinerary;

  const { isDirect } = searchParams;

  const { data } = useQuery<{ flightFare: IFare[] }>(GET_FLIGHT_FARE, {
    variables: {
      from: handleSeoul(departureCity?.code || ''),
      to: arrivalCity?.code || '',
      mode: tripType === TripType.RT ? 'depart' : 'oneWay',
      hoursBefore: 48,
      agent: 100,
      departDate: dep,
    },
    skip: isSkip,
  });

  if (!data) return [];

  return data.flightFare.reduce<CustomDate[]>((acc, { date: d, fares }) => {
    const fs = fares.reduce<number[]>((accu, { stops, fare }) => {
      // eslint-disable-next-line no-param-reassign
      if (fare) accu[stops] = fare;
      return accu;
    }, []);

    const lowestPrice = Math.round(
      ((fs.length && (isDirect ? fs[0] : Math.min(...fs.filter(f => f)))) ||
        0) / 1000,
    );
    if (lowestPrice > 0) {
      const price = lowestPrice.toString();
      const [int, fraction] = [price.slice(0, -1), price.slice(-1)];
      acc.push({
        date: d,
        name: int + (fraction !== '0' ? `.${fraction}만` : '만'),
      });
    }
    return acc;
  }, []);
};

export default useFarePrices;
